import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser } from '../constants/api';
import { COLORS } from '../constants/theme';

const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'number', label: 'At least one number', test: (p) => /[0-9]/.test(p) },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [hasCode, setHasCode] = useState(false);
  const [businessCode, setBusinessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isPasswordValid = () => passwordRules.every(r => r.test(password));

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) return setError('Full name is required.');
    if (!username.trim()) return setError('Username is required.');
    if (!email.trim() || !email.includes('@')) return setError('Enter a valid email address.');
    if (!isPasswordValid()) return setError('Password does not meet all requirements.');
    if (!hasCode && !businessName.trim()) return setError('Business name is required.');
    if (hasCode && !businessCode.trim()) return setError('Business code is required.');

    setLoading(true);
    setError('');
    try {
      const payload = {
        fullName: fullName.trim(),
        username: username.trim(),
        email: email.trim(),
        password,
        ...(hasCode ? { businessCode: businessCode.trim() } : { businessName: businessName.trim() }),
      };
      const res = await registerUser(payload);
      const data = res.data;

      if (data.devOtp) {
        alert(`Dev Mode: Your OTP is ${data.devOtp} — it has been noted, enter it on the next screen.`);
      }

      navigate('/verify', { state: { email: email.trim(), devOtp: data.devOtp } });
    } catch (err) {
      const msg = err?.response?.data ?? 'Something went wrong.';
      setError(typeof msg === 'string' ? msg : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: boxSizing: 'border-box' prevents padding from adding to width: '100%',
  //         which was causing inputs to overflow the grid columns and overlap labels.
  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    borderRadius: 10,
    border: `1.5px solid ${COLORS.border}`,
    fontSize: 14,
    color: COLORS.ink,
    background: COLORS.bg,
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 520, background: 'white', borderRadius: 20, padding: 40, border: `1px solid ${COLORS.border}`, boxShadow: '0 4px 40px rgba(0,0,0,0.06)' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'sans-serif', fontSize: 22, fontWeight: 900, marginBottom: 12 }}>
            <span style={{ color: COLORS.ink }}>Stok</span>
            <span style={{ color: COLORS.accent }}>Pilot</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: COLORS.ink, letterSpacing: -0.5 }}>Create your account</div>
          <div style={{ fontSize: 14, color: COLORS.inkLight, marginTop: 6 }}>Set up your business in seconds</div>
        </div>

        <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Full name + username row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Full Name</label>
              <input
                type="text" value={fullName} onChange={e => setFullName(e.target.value)}
                placeholder="Your full name" style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Username</label>
              <input
                type="text" value={username} onChange={e => setUsername(e.target.value)}
                placeholder="e.g. matthew01" style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Email Address</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
          </div>

          {/* Password */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Create a strong password"
                style={{ ...inputStyle, paddingRight: 44 }}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {password && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {passwordRules.map(rule => (
                  <div key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: rule.test(password) ? COLORS.accent : COLORS.danger }}>
                      {rule.test(password) ? '✓' : '✗'}
                    </span>
                    <span style={{ fontSize: 12, color: rule.test(password) ? COLORS.accent : COLORS.inkLight }}>
                      {rule.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Business code toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: COLORS.bg, borderRadius: 10, padding: '12px 14px', border: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.inkMid }}>Do you have a business code?</span>
            <button
              type="button"
              onClick={() => setHasCode(v => !v)}
              style={{ padding: '6px 16px', borderRadius: 20, border: `1.5px solid ${hasCode ? COLORS.accent : COLORS.border}`, background: hasCode ? COLORS.accent : 'transparent', color: hasCode ? 'white' : COLORS.inkMid, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {hasCode ? 'Yes ✓' : 'No'}
            </button>
          </div>

          {/* Business name or code */}
          {!hasCode ? (
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Business Name</label>
              <input
                type="text" value={businessName} onChange={e => setBusinessName(e.target.value)}
                placeholder="e.g. Chioma's Store" style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
              <div style={{ marginTop: 8, background: COLORS.accentLight, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: COLORS.accent, fontWeight: 600 }}>
                👑 You'll be the Owner. A business code will be generated for your staff.
              </div>
            </div>
          ) : (
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Business Code</label>
              <input
                type="text" value={businessCode} onChange={e => setBusinessCode(e.target.value.toUpperCase())}
                placeholder="e.g. MAT-7823" style={inputStyle}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
              <div style={{ marginTop: 8, background: '#EEF2FF', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#4B6BFB', fontWeight: 600 }}>
                👤 You'll join as Staff under this business.
              </div>
            </div>
          )}

          {error && (
            <div style={{ background: COLORS.dangerLight, border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!isPasswordValid() || loading}
            style={{ background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 800, cursor: (!isPasswordValid() || loading) ? 'not-allowed' : 'pointer', opacity: (!isPasswordValid() || loading) ? 0.6 : 1, marginTop: 4, fontFamily: 'inherit' }}
          >
            {loading ? 'Creating account...' : 'Create Account →'}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 14, color: COLORS.inkLight }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: COLORS.accent, fontWeight: 700, textDecoration: 'none' }}>Login</Link>
        </div>
      </div>
    </div>
  );
}